
import { Component, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

const AppDefaultFooter = () =>
	import(/* webpackChunkName: "default-footer" */ '@/components/footer/AppDefaultFooter.vue');
const AppFooterEuropeanBanner = () =>
	import(/* webpackChunkName: "european-banner-footer" */ '@/components/footer/AppFooterEuropeanBanner.vue');

import Provider from '@/interfaces/provider';
import { UrlMetadataResponse } from '@/interfaces/responses/url-metadata';

import { captureMessageForSentry } from '@/helpers/sentry-helper';
import {
	GetTopArticlesDocument,
	GetTopArticlesQuery,
	TopArticleFragment,
} from '@/pages/graphql/queries/GetTopArticles.query';
import { GetTopTitlesDocument, GetTopTitlesQuery } from '@/pages/graphql/queries/GetTopTitles.query';

const Constant = namespace('constant');
const Language = namespace('language');
const Routing = namespace('routing');
const User = namespace('user');

@Component({
	name: 'AppFooter',
	components: {
		AppDefaultFooter,
		AppFooterEuropeanBanner,
	},
	apollo: {
		topTitles: {
			query: GetTopTitlesDocument,
			variables() {
				return {
					country: this.country,
					language: this.language,
				};
			},
			update({ movie, show, upcomingMovie, upcomingShow }: GetTopTitlesQuery) {
				return {
					movie: movie?.edges?.map(({ node }) => node) ?? [],
					upcomingMovie: upcomingMovie?.edges?.map(({ node }) => node) ?? [],
					show: show?.edges?.map(({ node }) => node) ?? [],
					upcomingShow: upcomingShow?.edges?.map(({ node }) => node) ?? [],
				};
			},
			skip() {
				return !this.showDefaultFooter;
			},
			errorPolicy: 'all',
			error(error) {
				captureMessageForSentry(
					'[GraphQL Get Top Titles Data]:',
					{ error: error, where: 'Component: AppFooter' },
					'error'
				);
			},
		},
		topArticles: {
			query: GetTopArticlesDocument,
			variables() {
				return {
					country: this.country,
					first: 5,
				};
			},
			update({ articles }: GetTopArticlesQuery) {
				return (articles.edges ?? []).map(({ node }) => node);
			},
			skip() {
				return !this.showDefaultFooter;
			},
			errorPolicy: 'all',
			error(error) {
				captureMessageForSentry(
					'[GraphQL Get Top Articles Data]:',
					{ error: error, where: 'Component: AppFooter' },
					'error'
				);
			},
		},
	},
})
export default class AppFooter extends Vue {
	coldStart = true;
	metaReady: boolean = false;

	@Constant.Getter providers: () => Provider[];

	@Language.Getter locale: string;
	@Language.Getter country: string;
	@Language.Getter language: string;

	@Routing.State urlMetadataPromise: Promise<UrlMetadataResponse> | null;

	@User.Getter isUserOnboarded: boolean;

	topTitles = {
		movie: [],
		upcomingMovie: [],
		show: [],
		upcomingShow: [],
	};

	topArticles: TopArticleFragment[] = [];

	get showDefaultFooter() {
		return this.$route.name !== 'app.home';
	}

	async created() {
		await this.urlMetadataPromise;
		this.metaReady = true;
	}

	mounted() {
		this.coldStart = false;
	}
}
